import {
	autoUpdate,
	offset,
	shift,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import type { FieldValues, Path } from "react-hook-form";
import { useDisclosure } from "../hooks";
import { Icon } from "../icons";
import { Portal } from "../portal";

/** Props Interface */
interface FormErrorMessageProps<T extends FieldValues> {
	message: string;
	name: Path<T>;
}

export function FormErrorMessage<T extends FieldValues>({
	message,
	name,
}: FormErrorMessageProps<T>) {
	const disclosure = useDisclosure();
	const { refs, floatingStyles, context } = useFloating({
		placement: "bottom",
		whileElementsMounted: autoUpdate,
		middleware: [shift(), offset(5)],
		open: disclosure.isOpen,
		onOpenChange: disclosure.setIsOpen,
	});
	const hover = useHover(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

	/** Render */
	return (
		<React.Fragment>
			<div
				className="absolute -bottom-1 -right-1 p-2 cursor-pointer"
				ref={refs.setReference}
				{...getReferenceProps()}
			>
				<Icon
					name="AlertCircle"
					className="stroke-red-500 fill-red-500/10"
				/>
			</div>
			<AnimatePresence>
				{disclosure.isOpen && (
					<Portal>
						<motion.div
							className="card w-min min-w-[200px] ring-2 ring-red-500/20 border-red-500 z-[5000]"
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							initial={{ opacity: 0, marginTop: 10 }}
							animate={{ opacity: 1, marginTop: 0 }}
							exit={{ opacity: 0, marginTop: 10 }}
						>
							<label
								className="font-medium text-xs form-error"
								htmlFor={name}
							>
								{message}
							</label>
						</motion.div>
					</Portal>
				)}
			</AnimatePresence>
		</React.Fragment>
	);
}
